import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mic, MicOff, Video as VideoIcon, VideoOff, AlertCircle, Loader2, Check, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Pre-interview device check.
 * Shows a mirrored camera preview + live mic level meter. User confirms with
 * their chosen devices before we hand off to LiveKit.
 *
 * Props:
 *   name:      string — display name to show the user
 *   onReady:   ({ videoDeviceId, audioDeviceId, videoEnabled, audioEnabled }) => void
 *   onCancel:  () => void
 */
export default function DevicePreflight({ name, onReady, onCancel }) {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const rafRef = useRef(null);
    const audioCtxRef = useRef(null);

    const [phase, setPhase] = useState("acquiring"); // acquiring | ready | error
    const [error, setError] = useState("");
    const [videoDevices, setVideoDevices] = useState([]);
    const [audioDevices, setAudioDevices] = useState([]);
    const [videoDeviceId, setVideoDeviceId] = useState("");
    const [audioDeviceId, setAudioDeviceId] = useState("");
    const [videoEnabled, setVideoEnabled] = useState(true);
    const [audioEnabled, setAudioEnabled] = useState(true);
    const [micLevel, setMicLevel] = useState(0);

    // Attach the audio analyzer to the current stream so the mic bar responds.
    const attachAnalyzer = useCallback((stream) => {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            audioCtxRef.current = ctx;
            const src = ctx.createMediaStreamSource(stream);
            const analyzer = ctx.createAnalyser();
            analyzer.fftSize = 512;
            src.connect(analyzer);
            const buf = new Uint8Array(analyzer.frequencyBinCount);
            const tick = () => {
                analyzer.getByteTimeDomainData(buf);
                // RMS around 128 (silence baseline)
                let sum = 0;
                for (let i = 0; i < buf.length; i++) {
                    const v = (buf[i] - 128) / 128;
                    sum += v * v;
                }
                const rms = Math.sqrt(sum / buf.length);
                setMicLevel(Math.min(100, Math.round(rms * 400)));
                rafRef.current = requestAnimationFrame(tick);
            };
            tick();
        } catch (e) {
            // Analyzer is decorative; ignore failures.
        }
    }, []);

    // Idempotent teardown — called from acquire(), proceed(), and unmount.
    // AudioContext.close() is async and rejects if the context is already
    // closed; without the guard we get an uncaught runtime error that
    // covers the LiveKit room with the CRA dev overlay.
    const teardown = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        const ctx = audioCtxRef.current;
        audioCtxRef.current = null;
        if (ctx && ctx.state !== "closed") {
            ctx.close().catch(() => { /* already-closed race — ignore */ });
        }
    }, []);

    const acquire = useCallback(async (opts = {}) => {
        setPhase("acquiring");
        setError("");
        teardown();
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: opts.videoDeviceId ? { deviceId: { exact: opts.videoDeviceId } } : true,
                audio: opts.audioDeviceId ? { deviceId: { exact: opts.audioDeviceId } } : true,
            });
            streamRef.current = stream;
            if (videoRef.current) videoRef.current.srcObject = stream;
            // Enumerate now that permissions are granted so device labels are populated
            const all = await navigator.mediaDevices.enumerateDevices();
            const cams = all.filter((d) => d.kind === "videoinput");
            const mics = all.filter((d) => d.kind === "audioinput");
            setVideoDevices(cams);
            setAudioDevices(mics);
            const currentVideoTrack = stream.getVideoTracks()[0];
            const currentAudioTrack = stream.getAudioTracks()[0];
            if (currentVideoTrack) setVideoDeviceId(currentVideoTrack.getSettings().deviceId || cams[0]?.deviceId || "");
            if (currentAudioTrack) setAudioDeviceId(currentAudioTrack.getSettings().deviceId || mics[0]?.deviceId || "");
            attachAnalyzer(stream);
            setPhase("ready");
        } catch (e) {
            setError(
                e?.name === "NotAllowedError"
                    ? "Camera / microphone permission denied. Click the lock icon in the address bar and allow access, then retry."
                    : e?.name === "NotFoundError"
                        ? "No camera or microphone found. Plug one in and retry."
                        : "Could not access media devices. Close any other app (Zoom, Teams, another tab) using your camera and retry."
            );
            setPhase("error");
        }
    }, [attachAnalyzer, teardown]);

    useEffect(() => {
        acquire({});
        return teardown;
    }, [acquire, teardown]);

    // Toggle track enabled without stopping the stream
    useEffect(() => {
        streamRef.current?.getVideoTracks().forEach((t) => (t.enabled = videoEnabled));
    }, [videoEnabled]);
    useEffect(() => {
        streamRef.current?.getAudioTracks().forEach((t) => (t.enabled = audioEnabled));
    }, [audioEnabled]);

    const changeVideo = (id) => { setVideoDeviceId(id); acquire({ videoDeviceId: id, audioDeviceId }); };
    const changeAudio = (id) => { setAudioDeviceId(id); acquire({ videoDeviceId, audioDeviceId: id }); };

    const proceed = () => {
        // Free the preview stream — LiveKit will re-open its own on the same devices.
        teardown();
        onReady({ videoDeviceId, audioDeviceId, videoEnabled, audioEnabled });
    };

    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 text-slate-100" data-testid="device-preflight">
            <div className="w-full max-w-3xl bg-slate-900 rounded-2xl border border-slate-800 shadow-2xl overflow-hidden">
                <div className="px-6 pt-6 pb-4 border-b border-slate-800">
                    <h1 className="font-heading font-semibold text-xl">Ready to join the interview?</h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Let&apos;s test your camera and mic first. Nobody else will see this preview.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-5">
                    {/* Camera preview */}
                    <div className="md:col-span-3 bg-black relative aspect-video md:aspect-auto md:min-h-[320px]">
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            className="w-full h-full object-cover"
                            style={{ transform: "scaleX(-1)" }}  /* mirror */
                            data-testid="preflight-video"
                        />
                        {phase === "acquiring" && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                                <Loader2 size={22} className="animate-spin text-emerald-400" />
                            </div>
                        )}
                        {phase === "error" && (
                            <div className="absolute inset-0 flex items-center justify-center p-6 bg-black/85">
                                <div className="text-center">
                                    <AlertCircle size={22} className="mx-auto text-rose-500" />
                                    <p className="mt-2 text-sm">{error}</p>
                                    <Button size="sm" onClick={() => acquire({})} className="mt-3" data-testid="preflight-retry">Retry</Button>
                                </div>
                            </div>
                        )}
                        {!videoEnabled && phase === "ready" && (
                            <div className="absolute inset-0 flex items-center justify-center bg-slate-950/90">
                                <VideoOff size={40} className="text-slate-500" />
                            </div>
                        )}
                        {/* Track toggles overlay */}
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
                            <button
                                onClick={() => setAudioEnabled((v) => !v)}
                                disabled={phase !== "ready"}
                                className={`h-10 w-10 rounded-full flex items-center justify-center border transition disabled:opacity-40 disabled:cursor-not-allowed ${audioEnabled ? "bg-slate-800 border-slate-700 text-white" : "bg-rose-500 border-rose-500 text-white"}`}
                                data-testid="preflight-mic-toggle"
                                aria-label={audioEnabled ? "Mute mic" : "Unmute mic"}
                            >
                                {audioEnabled ? <Mic size={16} /> : <MicOff size={16} />}
                            </button>
                            <button
                                onClick={() => setVideoEnabled((v) => !v)}
                                disabled={phase !== "ready"}
                                className={`h-10 w-10 rounded-full flex items-center justify-center border transition disabled:opacity-40 disabled:cursor-not-allowed ${videoEnabled ? "bg-slate-800 border-slate-700 text-white" : "bg-rose-500 border-rose-500 text-white"}`}
                                data-testid="preflight-camera-toggle"
                                aria-label={videoEnabled ? "Turn camera off" : "Turn camera on"}
                            >
                                {videoEnabled ? <VideoIcon size={16} /> : <VideoOff size={16} />}
                            </button>
                        </div>
                    </div>

                    {/* Right column: device pickers + mic level */}
                    <div className="md:col-span-2 p-5 space-y-4">
                        <div>
                            <label className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Joining as</label>
                            <div className="mt-1 font-heading font-medium">{name || "Interview participant"}</div>
                        </div>

                        <div>
                            <label className="text-xs uppercase font-semibold text-slate-500 tracking-wider flex items-center gap-1.5"><VideoIcon size={11} /> Camera</label>
                            <select
                                value={videoDeviceId}
                                onChange={(e) => changeVideo(e.target.value)}
                                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-md px-2 py-1.5 text-sm"
                                disabled={phase !== "ready"}
                                data-testid="preflight-video-select"
                            >
                                {videoDevices.length === 0 && <option value="">Default</option>}
                                {videoDevices.map((d) => (
                                    <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${d.deviceId.slice(0, 6)}`}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="text-xs uppercase font-semibold text-slate-500 tracking-wider flex items-center gap-1.5"><Mic size={11} /> Microphone</label>
                            <select
                                value={audioDeviceId}
                                onChange={(e) => changeAudio(e.target.value)}
                                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-md px-2 py-1.5 text-sm"
                                disabled={phase !== "ready"}
                                data-testid="preflight-audio-select"
                            >
                                {audioDevices.length === 0 && <option value="">Default</option>}
                                {audioDevices.map((d) => (
                                    <option key={d.deviceId} value={d.deviceId}>{d.label || `Mic ${d.deviceId.slice(0, 6)}`}</option>
                                ))}
                            </select>
                            {/* Live mic level meter */}
                            <div className="mt-2">
                                <div className="text-[11px] text-slate-500 mb-1">
                                    {audioEnabled ? "Speak to test — bar should move" : "Mic muted"}
                                </div>
                                <div className="h-2 rounded-full bg-slate-800 overflow-hidden" data-testid="preflight-mic-meter">
                                    <div
                                        className={`h-full transition-[width] duration-75 ${micLevel > 60 ? "bg-emerald-500" : micLevel > 20 ? "bg-emerald-400/70" : "bg-slate-600"}`}
                                        style={{ width: `${audioEnabled ? micLevel : 0}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between gap-3 bg-slate-950/40">
                    <Button variant="ghost" onClick={onCancel} data-testid="preflight-cancel" className="text-slate-400 hover:text-white">Cancel</Button>
                    <div className="flex items-center gap-3">
                        {phase === "ready" && (
                            <div className="hidden sm:flex items-center gap-1.5 text-xs text-emerald-400">
                                <Check size={12} /> Devices look good
                            </div>
                        )}
                        <Button
                            onClick={proceed}
                            disabled={phase !== "ready"}
                            data-testid="preflight-join-btn"
                            className="bg-emerald-500 hover:bg-emerald-600 text-slate-950"
                        >
                            Join interview <ChevronRight size={14} className="ml-1" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
